import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardHeader from "../Dashboard/DashboardHeader";
import {
  ApplicationType,
  entity_id,
  generateOrderId,
  locationGeo,
  Platform,
} from "../Utils/Const";
import { useSelector } from "react-redux";
import { baseapi } from "../Environmentalurl";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function SendMoney() {
  const navigate = useNavigate();
  const location = useLocation();
  const beneficiary = location.state?.beneficiary;

  // Determine if this is an MMID transfer
  const isMMID = beneficiary?.type === "MMID";

  // ================= STATE =================
  const [amount, setAmount] = React.useState("");
  const [chargesData, setChargesData] = React.useState(null);
  const [openCharges, setOpenCharges] = React.useState(false);
  const [openTpin, setOpenTpin] = React.useState(false);
  const [receiverMobile, setReceiverMobile] = useState("");
  const [tpin, setTpin] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const senderName = sessionStorage.getItem("NameOnCard")?.replace(/"/g, "");
  const senderMMID = useSelector((state) => state.auth.user?.MMID);
  const senderAccount = useSelector((state) => state.auth.user?.AccountNo);
  const ip = useIPAddress();

  const walletTypeID = sessionStorage.getItem("WalletTypeID");
  const [form] = React.useState({
    name: beneficiary?.name || "",
    bank: beneficiary?.bank || "",
    account: beneficiary?.acc || "",
    confirmAccount: beneficiary?.acc || "",
    ifsc: beneficiary?.ifsc || "",
    limit: beneficiary?.limit || "",
    mobile: beneficiary?.mobile || "",
    mmid: beneficiary?.mmid || "",
  });

  // ================= FIELD STYLE HELPER =================
  const fieldSx = {
    "& .MuiOutlinedInput-root": { height: { xs: 36, md: 48 } },
    "& .MuiOutlinedInput-input": {
      fontSize: { xs: 12, md: 16 },
      py: { xs: 1, md: 1.5 },
    },
  };

  const labelSx = { fontWeight: 600, fontSize: { xs: 10, md: 14 } };

  // ================= VALIDATE PAYMENT (CHARGES) =================
  const handleValidatePayment = async () => {
    
    try {
      setLoading(true);

      const res = await api.post(
        `${baseapi}/janAadhaareWalletValidatePayment`,
        {
          ReqType: "Jan_Aadhaar_eWallet_Validate_Payment",
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
          Platform: Platform,
          ApplicationType: ApplicationType,
          GeoLocation: locationGeo,
          MachineID: ip,
          EntityID: entity_id,
          SessionID: sessionStorage.getItem("SessionID"),
          ServiceID: "9",
          TrxnAmount: amount,
        },
      );

      if (res.data?.Response_Code === "1000") {
        setChargesData(res.data);
        setOpenCharges(true);
      } else {
        alert(res.data?.Message || "Validation failed");
      }
    } catch (err) {
      alert("Charges validation failed");
    } finally {
      setLoading(false);
    }
  };

  // ================= IMPS PAYMENT (IFSC) =================
  const handleIMPSPayment = async () => {
    try {
      setLoading(true);

      const walletTypeID = sessionStorage.getItem("WalletTypeID");

      let payload = {};
      let apiUrl = "";

      if (walletTypeID === "0") {
        // Customer Wallet IMPS
        payload = {
          ReqType: "Jan_Aadhaar_eWallet_IMPS",
          Statement: "IFSC",
          ApplicationType,
          Platform,
          EntityID: entity_id,
          SessionID: sessionStorage.getItem("SessionID"),
          ServiceID: "9",
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
          Customer_Mobile_No: sessionStorage.getItem("mobileNumber"),
          Sender_Name: senderName,
          Sender_MMID: senderMMID,
          Sender_VirtualAccountNumber: senderAccount || "",
          BeneficiaryName: form.name,
          BeneficiaryBank: form.bank,
          PANNumber: sessionStorage.getItem("pan")?.replace(/"/g, ""),
          BeneficiaryAccountNumber: form.account,
          BeneficiaryIFSC: form.ifsc,
          BeneficiaryMobileNumber: sessionStorage.getItem("mobileNumber"),
          BeneficiaryMMID: "",
          TrxnAmount: Number(amount),
          Narration: `IMPS Transfer of Rs.${amount} to ${form.name}`,
          OrderID: generateOrderId({}),
          TPIN: tpin,
          GeoLocation: locationGeo,
          MachineID: ip,
          PublicIP: "106.78.1.23",
          PrivateIP: "106.78.1.23",
        };

        apiUrl = `${baseapi}/JanAadhaareWalletIMPS`;
      } else if (walletTypeID === "1") {
        // Agent Wallet IMPS
        payload = {
          Platform,
          Narration: `IMPS Transfer of Rs.${amount} to ${form.name}`,
          TPIN: tpin,
          OrderID: generateOrderId({}),
          EntityID: entity_id,
          PANNumber: sessionStorage.getItem("pan")?.replace(/"/g, ""),
          BeneficiaryMMID: "",
          Sender_MMID: senderMMID,
          ReqType: "TAOneappAgentIMPS",
          TrxnAmount: amount.toString(),
          MachineID: ip,
          BeneficiaryIFSC: form.ifsc,
          Statement: "IFSC",
          BeneficiaryName: form.name,
          Sender_Name: senderName,
          ServiceID: "9",
          ApplicationType,
          BeneficiaryBank: form.bank,
          BeneficiaryAccountNumber: form.account,
          Customer_Mobile_No: sessionStorage.getItem("mobileNumber"),
          Sender_VirtualAccountNumber: senderAccount || "",
          BeneficiaryMobileNumber: "",
          ReciverMobileNumber: receiverMobile,
          MobileNumber: sessionStorage.getItem("mobileNumber"),
          GeoLocation: locationGeo,
          SessionID: sessionStorage.getItem("SessionID"),
          Mobilenumber: sessionStorage.getItem("mobileNumber"),
        };

        apiUrl = `${baseapi}/agentimps`;
      } else {
        throw new Error("Invalid WalletTypeID");
      }

      const res = await api.post(apiUrl, payload);

      setPaymentResult({
        open: true,
        success: res.data?.Response_Code === "1000",
        message: res.data?.Message || "Payment completed",
      });
    } catch (err) {
      console.error(err);

      setPaymentResult({
        open: true,
        success: false,
        message: "Payment failed. Please try again.",
      });
    } finally {
      setLoading(false);
      setOpenTpin(false);
    }
  };

  // ================= MMID PAYMENT =================
  const handleMMIDPayment = async () => {
    try {
      setLoading(true);

      const payload = {
        ReqType: "Jan_Aadhaar_eWallet_IMPS",
        Statement: "MMID",
        ApplicationType,
        Platform,
        EntityID: entity_id,
        SessionID: sessionStorage.getItem("SessionID"),
        ServiceID: "9",
        Mobilenumber: sessionStorage.getItem("mobileNumber"),
        Customer_Mobile_No: sessionStorage.getItem("mobileNumber"),
        Sender_Name: senderName,
        Sender_MMID: senderMMID,
        Sender_VirtualAccountNumber: senderAccount || "",
        BeneficiaryName: form.name,
        BeneficiaryBank: form.bank,
        PANNumber: sessionStorage.getItem("pan")?.replace(/"/g, ""),
        BeneficiaryAccountNumber: "",
        BeneficiaryIFSC: "",
        BeneficiaryMobileNumber: form.mobile,
        BeneficiaryMMID: form.mmid,
        TrxnAmount: Number(amount),
        Narration: `MMID Transfer of Rs.${amount} to ${form.name}`,
        OrderID: generateOrderId({}),
        TPIN: tpin,
        GeoLocation: locationGeo,
        MachineID: ip,
        PublicIP: "106.78.1.23",
        PrivateIP: "106.78.1.23",
      };

      const res = await api.post(`${baseapi}/JanAadhaareWalletIMPS`, payload);

      setPaymentResult({
        open: true,
        success: res.data?.Response_Code === "1000",
        message: res.data?.Message || "Payment completed",
      });
    } catch (err) {
      setPaymentResult({
        open: true,
        success: false,
        message: "Payment failed. Please try again.",
      });
    } finally {
      setLoading(false);
      setOpenTpin(false);
    }
  };

  // Route to correct payment handler
  const handlePayment = () => {
    if (isMMID) {
      handleMMIDPayment();
    } else {
      handleIMPSPayment();
    }
  };

  const [paymentResult, setPaymentResult] = React.useState({
    open: false,
    success: false,
    message: "",
  });

  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

  return (
    <>
      <DashboardHeader />

      <Box bgcolor="#f6fafc" minHeight="100vh">
        <Box sx={{ maxWidth: "1200px", mx: "auto", px: 4, py: 5 }}>
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Box>
              <Typography
                color="primary"
                mb={1}
                sx={{
                  cursor: "pointer",
                  fontSize: { xs: 18, md: 13 },
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => navigate("/dashboard/transfertobank")}
              >
                ←
                <Box
                  component="span"
                  sx={{ display: { xs: "none", md: "inline" }, ml: 1 }}
                >
                  BACK TO TRANSFER
                </Box>
              </Typography>

              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ fontSize: { xs: 13, md: 14 }, whiteSpace: "nowrap" }}
              >
                Send Money
              </Typography>
            </Box>

            <Chip
              label="STEP 2 OF 2  —  Send Amount"
              sx={{
                bgcolor: "#e8f5e9",
                color: "#16a34a",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
            />
          </Box>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
              <Paper sx={{ p: 4, borderRadius: 3 }}>
                <Grid container spacing={3}>
                  {/* ===== IFSC FIELDS (only for IFSC type) ===== */}
                  {!isMMID && (
                    <>
                      <Grid size={{ md: 6, xs: 12 }} item xs={6}>
                        <Typography sx={labelSx}>ACCOUNT NUMBER</Typography>
                        <TextField
                          fullWidth
                          value={form.account}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={6}>
                        <Typography sx={labelSx}>CONFIRM ACCOUNT</Typography>
                        <TextField
                          fullWidth
                          value={form.confirmAccount}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={6}>
                        <Typography sx={labelSx}>IFSC CODE</Typography>
                        <TextField
                          fullWidth
                          value={form.ifsc}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={6}>
                        <Typography sx={labelSx}>BANK NAME</Typography>
                        <TextField
                          fullWidth
                          value={form.bank}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={6}>
                        <Typography sx={labelSx}>BENEFICIARY NAME</Typography>
                        <TextField
                          fullWidth
                          value={form.name}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>
                    </>
                  )}

                  {/* ===== MMID FIELDS (only for MMID type) ===== */}
                  {isMMID && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                        <Typography sx={labelSx}>BENEFICIARY NAME</Typography>
                        <TextField
                          fullWidth
                          value={form.name}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                        <Typography sx={labelSx}>MOBILE NUMBER</Typography>
                        <TextField
                          fullWidth
                          value={form.mobile}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                        <Typography sx={labelSx}>MMID</Typography>
                        <TextField
                          fullWidth
                          value={form.mmid}
                          InputProps={{ readOnly: true }}
                          sx={fieldSx}
                        />
                      </Grid>
                    </>
                  )}

                  {/* ===== AMOUNT (always shown) ===== */}
                  <Grid size={{ xs: 12, md: 6 }} item xs={12} md={6}>
                    <Typography sx={labelSx}>TRANSACTION AMOUNT</Typography>
                    <TextField
                      fullWidth
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      inputProps={{ inputMode: "numeric" }}
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }} item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={loading || !amount}
                      onClick={handleValidatePayment}
                      sx={{
                        bgcolor: "#22c55e",
                        py: 1.5,
                        fontSize: { xs: 10, md: 16 },
                        "&:hover": { bgcolor: "#16a34a" },
                      }}
                    >
                      {loading ? "PROCESSING..." : "PROCEED TO PAY"}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "#0f172a",
                  color: "#fff",
                }}
              >
                <Typography fontWeight="bold">Secure Transaction</Typography>
                <Typography fontSize={14}>• Encrypted</Typography>
                <Typography fontSize={14}>• RBI compliant</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ================= CHARGES POPUP ================= */}
      <Dialog
        open={openCharges}
        onClose={() => setOpenCharges(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1.5 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize={18}>
            Payment Summary
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            Review charges before proceeding
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Box sx={{ bgcolor: "#f5f7fb", borderRadius: 2, p: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Amount</Typography>
              <Typography fontWeight={600}>
                ₹{chargesData?.TransactionAmount}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Charges</Typography>
              <Typography fontWeight={600}>₹{chargesData?.Charges}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">GST</Typography>
              <Typography fontWeight={600}>₹{chargesData?.GST}</Typography>
            </Box>
            <Box mt={2} pt={1} borderTop="1px dashed #ccc">
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight={700}>Total Payable</Typography>
                <Typography fontWeight={700} color="primary">
                  ₹{chargesData?.TotalPayable}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenCharges(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              boxShadow: "0 6px 16px rgba(34,197,94,.3)",
            }}
            onClick={() => {
              setOpenCharges(false);
              setOpenTpin(true);
            }}
          >
            Confirm & Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= TPIN POPUP ================= */}
      <Dialog
        open={openTpin}
        onClose={() => setOpenTpin(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1.5 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize={18}>
            {walletTypeID === "1" ? "Agent IMPS Payment" : "Enter TPIN"}
          </Typography>

          <Typography fontSize={13} color="text.secondary">
            {walletTypeID === "1"
              ? "Enter Receiver Mobile Number and your 4-digit TPIN"
              : "For your security, please enter your 4-digit TPIN"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Show only for Agent Wallet */}
            {walletTypeID === "1" && (
              <TextField
                fullWidth
                label="Receiver Mobile Number"
                value={receiverMobile}
                onChange={(e) =>
                  setReceiverMobile(
                    e.target.value.replace(/\D/g, "").slice(0, 10),
                  )
                }
                inputProps={{
                  maxLength: 10,
                }}
              />
            )}

            <TextField
              fullWidth
              type="password"
              label="4-Digit TPIN"
              value={tpin}
              onChange={(e) =>
                setTpin(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              inputProps={{
                maxLength: 4,
                style: {
                  textAlign: "center",
                  letterSpacing: "10px",
                  fontSize: 20,
                  fontWeight: 600,
                },
              }}
            />

            <Typography fontSize={12} color="text.secondary" textAlign="center">
              Never share your TPIN with anyone
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenTpin(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={
              loading ||
              tpin.length !== 4 ||
              (walletTypeID === "1" && receiverMobile.length !== 10)
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              boxShadow:
                tpin.length === 4 ? "0 6px 16px rgba(34,197,94,.35)" : "none",
            }}
            onClick={handlePayment}
          >
            {loading ? "PROCESSING..." : "Pay Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= RESULT POPUP ================= */}
      <Dialog
        open={paymentResult.open}
        onClose={() => setPaymentResult({ ...paymentResult, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography
            fontWeight={700}
            color={paymentResult.success ? "#16a34a" : "#dc2626"}
          >
            {paymentResult.success ? "Payment Successful" : "Payment Failed"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography textAlign="center" mt={1}>
            {paymentResult.success
              ? "Your payment was successful."
              : paymentResult.message}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: paymentResult.success ? "#22c55e" : "#ef4444",
              "&:hover": {
                bgcolor: paymentResult.success ? "#16a34a" : "#dc2626",
              },
            }}
            onClick={() => {
              setPaymentResult({ ...paymentResult, open: false });
              if (paymentResult.success) navigate("/dashboard");
            }}
          >
            {paymentResult.success ? "Back to Dashboard" : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
